import React, { useMemo } from 'react';
import { Text } from '@vibe/core';
import { NavigationChevronRight, NavigationChevronDown } from '@vibe/icons';
import { formatHours } from '../../utils/timesheet-capacity';
import type {
  ProgressGroup,
  ProgressRow,
  ProgressViewType,
} from '../../types/timesheet.types';

// ─── View configuration per progress type ────────────────────────────────────

interface ViewConfig {
  col1Label: string;
  col2Label: string;
  barLabel: string;
  getCol1: (r: ProgressRow) => number;
  getCol2: (r: ProgressRow) => number;
  getPercent: (r: ProgressRow) => number;
  getGroupCol1: (g: ProgressGroup) => number;
  getGroupCol2: (g: ProgressGroup) => number;
  getGroupPercent: (g: ProgressGroup) => number;
}

const VIEW_CONFIG: Record<ProgressViewType, ViewConfig> = {
  progress_timesheet: {
    col1Label: 'Required\nHours (h)',
    col2Label: 'Logged\nHours (h)',
    barLabel: 'Timesheet Progress',
    getCol1: (r) => r.requiredHours,
    getCol2: (r) => r.loggedHours,
    getPercent: (r) =>
      r.requiredHours > 0 ? (r.loggedHours / r.requiredHours) * 100 : 0,
    getGroupCol1: (g) => g.totalRequiredHours,
    getGroupCol2: (g) => g.totalLoggedHours,
    getGroupPercent: (g) =>
      g.totalRequiredHours > 0
        ? (g.totalLoggedHours / g.totalRequiredHours) * 100
        : 0,
  },
  progress_utilization: {
    col1Label: 'Required\nHours (h)',
    col2Label: 'Planned\nHours (h)',
    barLabel: 'Required vs Planned',
    getCol1: (r) => r.requiredHours,
    getCol2: (r) => r.plannedHours,
    getPercent: (r) =>
      r.requiredHours > 0 ? (r.plannedHours / r.requiredHours) * 100 : 0,
    getGroupCol1: (g) => g.totalRequiredHours,
    getGroupCol2: (g) => g.totalPlannedHours,
    getGroupPercent: (g) =>
      g.totalRequiredHours > 0
        ? (g.totalPlannedHours / g.totalRequiredHours) * 100
        : 0,
  },
  progress_planned_vs_actual: {
    col1Label: 'Planned\nHours (h)',
    col2Label: 'Logged\nHours (h)',
    barLabel: 'Planned vs Actual',
    getCol1: (r) => r.plannedHours,
    getCol2: (r) => r.loggedHours,
    getPercent: (r) =>
      r.plannedHours > 0 ? (r.loggedHours / r.plannedHours) * 100 : 0,
    getGroupCol1: (g) => g.totalPlannedHours,
    getGroupCol2: (g) => g.totalLoggedHours,
    getGroupPercent: (g) =>
      g.totalPlannedHours > 0
        ? (g.totalLoggedHours / g.totalPlannedHours) * 100
        : 0,
  },
};

// ─── Bar color helper ────────────────────────────────────────────────────────

function getBarClass(percent: number): string {
  if (percent > 100) return 'rp-ts-progress-table__bar-fill--over';
  if (percent >= 80) return 'rp-ts-progress-table__bar-fill--good';
  return 'rp-ts-progress-table__bar-fill--low';
}

function formatPercent(value: number): string {
  return value.toFixed(2);
}

// ─── Component ───────────────────────────────────────────────────────────────

export interface ProgressTableProps {
  groups: ProgressGroup[];
  progressType: ProgressViewType;
  hasSelection: boolean;
}

const ProgressTable: React.FC<ProgressTableProps> = ({
  groups,
  progressType,
  hasSelection,
}) => {
  const cfg = VIEW_CONFIG[progressType];

  // Sort rows within each group by percentage descending
  const sortedGroups = useMemo(() => {
    return groups.map((g) => ({
      ...g,
      rows: [...g.rows].sort(
        (a, b) => cfg.getPercent(b) - cfg.getPercent(a),
      ),
    }));
  }, [groups, cfg]);

  // Check if billable data is available
  const hasBillableData = useMemo(() => {
    return groups.some((g) => g.rows.some((r) => r.billableHours != null));
  }, [groups]);

  // Compute grand totals
  const grandTotals = useMemo(() => {
    const allRows = groups.flatMap((g) => g.rows);
    const totalCol1 = allRows.reduce((sum, r) => sum + cfg.getCol1(r), 0);
    const totalCol2 = allRows.reduce((sum, r) => sum + cfg.getCol2(r), 0);
    const totalPercent = totalCol1 > 0 ? (totalCol2 / totalCol1) * 100 : 0;
    const totalBillable = hasBillableData
      ? Math.round(allRows.reduce((sum, r) => sum + (r.billableHours ?? 0), 0) * 100) / 100
      : null;
    return {
      col1: Math.round(totalCol1 * 100) / 100,
      col2: Math.round(totalCol2 * 100) / 100,
      percent: totalPercent,
      billable: totalBillable,
    };
  }, [groups, cfg, hasBillableData]);

  const hasMultipleGroups = sortedGroups.length > 1;

  if (groups.length === 0 || groups.every((g) => g.rows.length === 0)) {
    return (
      <div className="rp-ts-timeline__empty">
        <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
          {hasSelection
            ? 'No data found for the selected period.'
            : 'Select teams or users to view progress data.'}
        </Text>
      </div>
    );
  }

  return (
    <div className="rp-ts-progress-table">
      <div className="rp-ts-progress-table__scroll">
        <table className="rp-ts-progress-table__table">
          <thead>
            <tr>
              <th className="rp-ts-progress-table__th rp-ts-progress-table__col-user">
                User
              </th>
              <th className="rp-ts-progress-table__th rp-ts-progress-table__col-hours">
                {cfg.col1Label.replace('\n', ' ')}
              </th>
              <th className="rp-ts-progress-table__th rp-ts-progress-table__col-hours">
                {cfg.col2Label.replace('\n', ' ')}
              </th>
              {hasBillableData && (
                <th className="rp-ts-progress-table__th rp-ts-progress-table__col-hours">
                  Billable Hours (h)
                </th>
              )}
              <th className="rp-ts-progress-table__th rp-ts-progress-table__col-bar">
                {cfg.barLabel} (%)
              </th>
            </tr>
          </thead>

          <tbody>
            {sortedGroups.map((group) => (
              <React.Fragment key={group.groupId}>
                {/* Group header row */}
                {hasMultipleGroups && (
                  <tr className="rp-ts-progress-table__group-row">
                    <td colSpan={hasBillableData ? 5 : 4}>
                      <div className="rp-ts-progress-table__group-header">
                        <NavigationChevronDown />
                        <Text
                          type={Text.types.TEXT2}
                          weight={Text.weights.BOLD}
                        >
                          {group.groupName}
                        </Text>
                        <span className="rp-ts-progress-table__group-count">
                          ({group.rows.length} items)
                        </span>
                      </div>
                    </td>
                  </tr>
                )}

                {/* User rows */}
                {group.rows.map((row) => {
                  const percent = cfg.getPercent(row);
                  return (
                    <tr key={row.userId} className="rp-ts-progress-table__row">
                      <td className="rp-ts-progress-table__td rp-ts-progress-table__col-user">
                        <div className="rp-ts-progress-table__user-cell">
                          <span className="rp-ts-entity__avatar" title={row.userName}>
                            {row.userName.charAt(0).toUpperCase()}
                          </span>
                          <span className="rp-ts-progress-table__user-name" title={row.userName}>
                            {row.userName}
                          </span>
                        </div>
                      </td>
                      <td className="rp-ts-progress-table__td rp-ts-progress-table__col-hours">
                        <span className="rp-ts-progress-table__hours">
                          {formatHours(cfg.getCol1(row))}
                        </span>
                      </td>
                      <td className="rp-ts-progress-table__td rp-ts-progress-table__col-hours">
                        <span className="rp-ts-progress-table__hours">
                          {formatHours(cfg.getCol2(row))}
                        </span>
                      </td>
                      {hasBillableData && (
                        <td className="rp-ts-progress-table__td rp-ts-progress-table__col-hours">
                          <span className="rp-ts-progress-table__hours" style={{ color: '#00854d' }}>
                            {row.billableHours != null ? formatHours(row.billableHours) : '-'}
                          </span>
                        </td>
                      )}
                      <td className="rp-ts-progress-table__td rp-ts-progress-table__col-bar">
                        <div className="rp-ts-progress-table__bar">
                          <div
                            className={`rp-ts-progress-table__bar-fill ${getBarClass(percent)}`}
                            style={{
                              width: `${Math.min(percent, 100)}%`,
                            }}
                          />
                          <span className="rp-ts-progress-table__bar-text">
                            {formatPercent(percent)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {/* Group totals (if multiple groups) */}
                {hasMultipleGroups && (
                  <tr className="rp-ts-progress-table__group-total-row">
                    <td className="rp-ts-progress-table__td" />
                    <td className="rp-ts-progress-table__td rp-ts-progress-table__col-hours">
                      <span className="rp-ts-progress-table__hours rp-ts-progress-table__hours--bold">
                        {formatHours(cfg.getGroupCol1(group))}
                      </span>
                    </td>
                    <td className="rp-ts-progress-table__td rp-ts-progress-table__col-hours">
                      <span className="rp-ts-progress-table__hours rp-ts-progress-table__hours--bold">
                        {formatHours(cfg.getGroupCol2(group))}
                      </span>
                    </td>
                    {hasBillableData && (
                      <td className="rp-ts-progress-table__td rp-ts-progress-table__col-hours">
                        <span className="rp-ts-progress-table__hours rp-ts-progress-table__hours--bold" style={{ color: '#00854d' }}>
                          {group.totalBillableHours != null ? formatHours(group.totalBillableHours) : '-'}
                        </span>
                      </td>
                    )}
                    <td className="rp-ts-progress-table__td rp-ts-progress-table__col-bar">
                      <div className="rp-ts-progress-table__bar">
                        <div
                          className={`rp-ts-progress-table__bar-fill ${getBarClass(cfg.getGroupPercent(group))}`}
                          style={{
                            width: `${Math.min(cfg.getGroupPercent(group), 100)}%`,
                          }}
                        />
                        <span className="rp-ts-progress-table__bar-text rp-ts-progress-table__bar-text--bold">
                          {formatPercent(cfg.getGroupPercent(group))}
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>

          {/* Footer totals */}
          <tfoot>
            <tr className="rp-ts-progress-table__footer">
              <td className="rp-ts-progress-table__td rp-ts-progress-table__col-user">
                <Text
                  type={Text.types.TEXT2}
                  weight={Text.weights.BOLD}
                  style={{ color: '#676879' }}
                >
                  Total
                </Text>
              </td>
              <td className="rp-ts-progress-table__td rp-ts-progress-table__col-hours">
                <span className="rp-ts-progress-table__hours rp-ts-progress-table__hours--bold">
                  {formatHours(grandTotals.col1)}
                </span>
              </td>
              <td className="rp-ts-progress-table__td rp-ts-progress-table__col-hours">
                <span className="rp-ts-progress-table__hours rp-ts-progress-table__hours--bold">
                  {formatHours(grandTotals.col2)}
                </span>
              </td>
              {hasBillableData && (
                <td className="rp-ts-progress-table__td rp-ts-progress-table__col-hours">
                  <span className="rp-ts-progress-table__hours rp-ts-progress-table__hours--bold" style={{ color: '#00854d' }}>
                    {grandTotals.billable != null ? formatHours(grandTotals.billable) : '-'}
                  </span>
                </td>
              )}
              <td className="rp-ts-progress-table__td rp-ts-progress-table__col-bar">
                <div className="rp-ts-progress-table__bar">
                  <div
                    className={`rp-ts-progress-table__bar-fill ${getBarClass(grandTotals.percent)}`}
                    style={{
                      width: `${Math.min(grandTotals.percent, 100)}%`,
                    }}
                  />
                  <span className="rp-ts-progress-table__bar-text rp-ts-progress-table__bar-text--bold">
                    {formatPercent(grandTotals.percent)}
                  </span>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default ProgressTable;
